/**
* @license Apache-2.0
*
* Copyright (c) 2025 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //

import { factory as factory } from '@stdlib/ndarray-vector-ctor';


// MAIN //

/**
* Returns a boolean vector (i.e., a one-dimensional ndarray).
*
* @name BooleanVector
* @type {Function}
* @param {(NonNegativeInteger|Collection|ArrayBuffer|Iterable)} [arg] - length, typed array, array-like object, buffer, or iterable
* @param {NonNegativeInteger} [byteOffset=0] - byte offset
* @param {NonNegativeInteger} [length] - view length
* @param {Options} [options] - function options
* @param {boolean} [options.readonly=false] - boolean indicating whether to return a read-only vector
* @param {string} [options.mode='throw'] - specifies how to handle indices which exceed vector dimensions
* @param {string} [options.order='row-major'] - memory layout (either row-major or column-major)
* @throws {TypeError} first argument must be either a length, typed array, array-like object, buffer, iterable, or options object
* @throws {TypeError} must provide valid options
* @returns {ndarray} one-dimensional ndarray
*
* @example
* import numel from '@stdlib/ndarray-numel';
*
* var arr = new BooleanVector();
* // returns <ndarray>
*
* var len = numel( arr );
* // returns 0
*
* @example
* import numel from '@stdlib/ndarray-numel';
*
* var arr = new BooleanVector( 2 );
* // returns <ndarray>
*
* var len = numel( arr );
* // returns 2
*
* @example
* import numel from '@stdlib/ndarray-numel';
*
* var arr = new BooleanVector( [ true, false ] );
* // returns <ndarray>
*
* var len = numel( arr );
* // returns 2
*
* @example
* import numel from '@stdlib/ndarray-numel';
* import ArrayBuffer from '@stdlib/array-buffer';
*
* var buf = new ArrayBuffer( 32 );
* var arr = new BooleanVector( buf );
* // returns <ndarray>
*
* var len = numel( arr );
* // returns 32
*
* @example
* import numel from '@stdlib/ndarray-numel';
* import ArrayBuffer from '@stdlib/array-buffer';
*
* var buf = new ArrayBuffer( 32 );
* var arr = new BooleanVector( buf, 16 );
* // returns <ndarray>
*
* var len = numel( arr );
* // returns 16
*
* @example
* import numel from '@stdlib/ndarray-numel';
* import ArrayBuffer from '@stdlib/array-buffer';
*
* var buf = new ArrayBuffer( 64 );
* var arr = new BooleanVector( buf, 16, 2 );
* // returns <ndarray>
*
* var len = numel( arr );
* // returns 2
*/
var BooleanVector = factory( 'bool' );


// EXPORTS //

export default BooleanVector;
